import React, { useState } from "react";

import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import AccountBalanceWalletRoundedIcon from "@material-ui/icons/AccountBalanceWalletRounded";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";

import { useSetup } from "../hooks/useSetup";
import api from "../../../services/api";

import {
  KoraxOnboardingShell,
  KoraxOnboardingContent,
  KoraxOnboardingFooter
} from "../../../components/KoraxUI/Onboarding";

const paymentOptions = [
  {
    id: "pix",
    title: "Pix",
    description: "Pagamento instantâneo por chave Pix."
  },
  {
    id: "cartao",
    title: "Cartão",
    description: "Cartão de crédito ou débito."
  },
  {
    id: "dinheiro",
    title: "Dinheiro",
    description: "Pagamento em espécie."
  },
  {
    id: "transferencia",
    title: "Transferência",
    description: "Transferência bancária."
  },
  {
    id: "boleto",
    title: "Boleto",
    description: "Pagamento por boleto bancário."
  }
];

const pixKeyTypes = [
  { value: "cpf", label: "CPF" },
  { value: "cnpj", label: "CNPJ" },
  { value: "email", label: "E-mail" },
  { value: "telefone", label: "Telefone" },
  { value: "aleatoria", label: "Chave aleatória" }
];

const useStyles = makeStyles(theme => ({
  layout: {
    width: "100%",
    maxWidth: 900,
    display: "grid",
    gridTemplateColumns: "1fr .85fr",
    gap: 18,

    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr"
    }
  },

  panel: {
    padding: 22,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#FFFFFF"
  },

  title: {
    fontSize: 16,
    fontWeight: 800,
    color: theme.palette.text.primary
  },

  subtitle: {
    marginTop: 6,
    marginBottom: 18,
    fontSize: 12.5,
    lineHeight: 1.5,
    color: theme.palette.text.secondary
  },

  options: {
    display: "flex",
    flexDirection: "column",
    gap: 10
  },

  option: {
    padding: "8px 12px",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    transition: "all .2s ease"
  },

  optionSelected: {
    borderColor: "#155EEF",
    background: "rgba(21,94,239,.07)"
  },

  optionLabel: {
    width: "100%",
    margin: 0
  },

  optionContent: {
    display: "flex",
    flexDirection: "column"
  },

  optionTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: theme.palette.text.primary
  },

  optionDescription: {
    marginTop: 2,
    fontSize: 12,
    color: theme.palette.text.secondary
  },

  field: {
    marginBottom: 14,

    "& .MuiOutlinedInput-root": {
      borderRadius: 12
    }
  },

  pixBox: {
    marginTop: 16,
    paddingTop: 18,
    borderTop: `1px solid ${theme.palette.divider}`
  },

  empty: {
    minHeight: 240,
    padding: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    borderRadius: 14,
    background: "rgba(21,94,239,.05)",
    textAlign: "center"
  },

  emptyIcon: {
    marginBottom: 12,
    fontSize: 42,
    color: "#155EEF"
  },

  summary: {
    display: "flex",
    flexDirection: "column",
    gap: 10
  },

  summaryItem: {
    padding: 12,
    display: "flex",
    alignItems: "center",
    gap: 10,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`
  },

  checkIcon: {
    color: "#16A34A"
  },

  info: {
    width: "100%",
    maxWidth: 900,
    marginTop: 18,
    padding: 14,
    borderRadius: 12,
    background: "rgba(21,94,239,.06)"
  }
}));

export default function PaymentStep() {
  const classes = useStyles();

  const {
    company,
    ai,
    updateAI,
    nextStep,
    previousStep
  } = useSetup();

  const [paymentMethods, setPaymentMethods] = useState(
    Array.isArray(ai?.paymentMethods) ? ai.paymentMethods : []
  );

  const [pixKeyType, setPixKeyType] = useState(
    ai?.pixKeyType || ""
  );

  const [pixKey, setPixKey] = useState(
    ai?.pixKey || ""
  );

  const [pixReceiverName, setPixReceiverName] = useState(
    ai?.pixReceiverName || ""
  );

  const [isPreparing, setIsPreparing] = useState(false);

  const hasPix = paymentMethods.includes("pix");

  const togglePaymentMethod = method => {
    setPaymentMethods(previous =>
      previous.includes(method)
        ? previous.filter(item => item !== method)
        : [...previous, method]
    );
  };

  const handleContinue = async () => {
    if (!canContinue || isPreparing) {
      return;
    }

    const paymentData = {
      paymentMethods,
      pixKeyType: hasPix ? pixKeyType.trim() : "",
      pixKey: hasPix ? pixKey.trim() : "",
      pixReceiverName: hasPix ? pixReceiverName.trim() : ""
    };

    setIsPreparing(true);

    try {
      const { data } = await api.post("/onboarding/generate-agent", {
        promptId: Number(ai?.promptId || 0) || undefined,
        companyName:
          company?.name ||
          company?.companyName ||
          "Empresa",
        companySegment: company?.segment || "",
        agentDisplayName: ai?.agentDisplayName || "",
        openingMessage: ai?.openingMessage || "",
        businessDescription: ai?.businessDescription || "",
        mainProducts: ai?.mainProducts || "",
        importantInformation: ai?.importantInformation || "",
        salesProcess: ai?.salesProcess || "",
        closingMethod: ai?.closingMethod || "",
        serviceArea: ai?.serviceArea || "",
        salesDifferential: ai?.salesDifferential || "",
        objective: ai?.objective || "vendas",
        tone: ai?.tone || "humanizado",
        ...paymentData
      });

      const createdPromptId = Number(
        data?.promptId ||
        data?.id ||
        0
      );

      if (!createdPromptId) {
        throw new Error(
          "A API não retornou o identificador do agente."
        );
      }

      updateAI({
        ...paymentData,
        trained: true,
        promptId: createdPromptId
      });

      nextStep();
    } catch (error) {
      console.error(
        "[ONBOARDING] erro ao preparar agente",
        error
      );

      alert(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Não foi possível preparar sua IA. Tente novamente."
      );
    } finally {
      setIsPreparing(false);
    }
  };

  const selectedOptions = paymentOptions.filter(option =>
    paymentMethods.includes(option.id)
  );

  const canContinue =
    !hasPix ||
    (
      pixKeyType.trim().length > 0 &&
      pixKey.trim().length > 0 &&
      pixReceiverName.trim().length > 0
    );

  return (
    <KoraxOnboardingShell step={7} totalSteps={9}>
      <KoraxOnboardingContent
        eyebrow="Condições de pagamento"
        title="Como seus clientes podem pagar?"
        description="Essas informações ajudam sua IA a orientar o cliente corretamente, sem inventar condições ou dados de pagamento."
      >
        <div className={classes.layout}>
          <div className={classes.panel}>
            <Typography className={classes.title}>
              Formas aceitas
            </Typography>

            <Typography className={classes.subtitle}>
              Selecione todas as formas de pagamento disponíveis na empresa.
            </Typography>

            <div className={classes.options}>
              {paymentOptions.map(option => {
                const selected = paymentMethods.includes(option.id);

                return (
                  <div
                    key={option.id}
                    className={`${classes.option} ${
                      selected ? classes.optionSelected : ""
                    }`}
                  >
                    <FormControlLabel
                      className={classes.optionLabel}
                      control={
                        <Checkbox
                          color="primary"
                          checked={selected}
                          onChange={() => togglePaymentMethod(option.id)}
                        />
                      }
                      label={
                        <div className={classes.optionContent}>
                          <Typography className={classes.optionTitle}>
                            {option.title}
                          </Typography>

                          <Typography className={classes.optionDescription}>
                            {option.description}
                          </Typography>
                        </div>
                      }
                    />
                  </div>
                );
              })}
            </div>

            {hasPix && (
              <div className={classes.pixBox}>
                <Typography className={classes.title}>
                  Dados do Pix
                </Typography>

                <Typography className={classes.subtitle}>
                  Preencha os dados exatos que poderão ser informados ao cliente.
                </Typography>

                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  className={classes.field}
                  label="Tipo da chave Pix"
                  value={pixKeyType}
                  onChange={event => setPixKeyType(event.target.value)}
                >
                  {pixKeyTypes.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  variant="outlined"
                  className={classes.field}
                  label="Chave Pix"
                  placeholder="Digite a chave exatamente como deve ser enviada."
                  value={pixKey}
                  onChange={event => setPixKey(event.target.value)}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  className={classes.field}
                  label="Nome do recebedor"
                  placeholder="Nome que aparece para o cliente ao pagar."
                  value={pixReceiverName}
                  onChange={event => setPixReceiverName(event.target.value)}
                />
              </div>
            )}
          </div>

          <div className={classes.panel}>
            <Typography className={classes.title}>
              Resumo
            </Typography>

            <Typography className={classes.subtitle}>
              Confira as condições que serão salvas no treinamento inicial.
            </Typography>

            {selectedOptions.length === 0 ? (
              <div className={classes.empty}>
                <AccountBalanceWalletRoundedIcon
                  className={classes.emptyIcon}
                />

                <Typography variant="body2">
                  Nenhuma forma de pagamento selecionada.
                </Typography>
              </div>
            ) : (
              <div className={classes.summary}>
                {selectedOptions.map(option => (
                  <div className={classes.summaryItem} key={option.id}>
                    <CheckCircleRoundedIcon className={classes.checkIcon} />

                    <div>
                      <Typography className={classes.optionTitle}>
                        {option.title}
                      </Typography>

                      <Typography className={classes.optionDescription}>
                        {option.id === "pix" && pixKey
                          ? `Chave cadastrada: ${pixKey}`
                          : option.description}
                      </Typography>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={classes.info}>
          <Typography variant="body2">
            Nenhuma forma de pagamento será enviada automaticamente sem que o cliente solicite ou sem que a conversa esteja no momento adequado.
          </Typography>
        </div>
      </KoraxOnboardingContent>

      <KoraxOnboardingFooter
        onBack={previousStep}
        onNext={handleContinue}
        nextLabel={
          isPreparing
            ? "Preparando sua IA..."
            : "Preparar minha IA"
        }
        disableNext={!canContinue || isPreparing}
      />
    </KoraxOnboardingShell>
  );
}
